import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ShieldCheck, Trash2, Plus, Lock } from "lucide-react";
import { toast } from "sonner";

export default function AdminAllowlist() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [toRemove, setToRemove] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/allowlist");
      setData(r.data);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 403) toast.error("Only the owner can view this page");
      else toast.error(err?.response?.data?.detail || "Failed to load allowlist");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes("@")) { toast.error("Enter a valid email"); return; }
    setSaving(true);
    try {
      await api.post("/admin/allowlist", { email: clean, note });
      toast.success(`${clean} added to admin allowlist`);
      setEmail("");
      setNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally { setSaving(false); }
  };

  const removeEmail = async () => {
    if (!toRemove) return;
    try {
      await api.delete(`/admin/allowlist/${encodeURIComponent(toRemove)}`);
      toast.success(`${toRemove} removed. Existing sessions revoked.`);
      setToRemove(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  if (data && !data.you_are_owner) {
    return (
      <div className="p-8 space-y-4">
        <header>
          <div className="label-caps text-accent mb-2">Access</div>
          <h1 className="font-heading text-4xl tracking-tight text-primary">Admin Allowlist</h1>
        </header>
        <Card className="p-6 border-border flex items-center gap-3">
          <Lock size={20} className="text-muted-foreground" />
          <div className="text-sm">
            Only the owner (<span className="font-mono">{data.owner || "not configured"}</span>) can manage the allowlist.
            <br/>Ask them to add or remove teammate emails.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="label-caps text-accent mb-2">Access control</div>
          <h1 className="font-heading text-4xl tracking-tight text-primary">Admin Allowlist</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Anyone signing in with a Google account outside this list is denied at the login step.
            The owner (<span className="font-mono">{data?.owner}</span>) can add or revoke teammate access below.
          </p>
        </div>
      </header>

      <Card className="p-6 border-border">
        <div className="label-caps text-primary mb-4">Add a teammate</div>
        <div className="flex flex-col md:flex-row gap-3">
          <Input
            data-testid="allowlist-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@medishiv.com"
            className="h-11 rounded-lg md:max-w-sm"
          />
          <Input
            data-testid="allowlist-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Role / note (optional)"
            className="h-11 rounded-lg flex-1"
          />
          <Button
            data-testid="allowlist-add-btn"
            onClick={add}
            disabled={saving}
            className="h-11 rounded-lg bg-primary hover:bg-primary/90"
          >
            <Plus size={16} className="mr-1"/> Add
          </Button>
        </div>
      </Card>

      <Card className="border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="label-caps text-primary">Current allowlist</div>
          <Badge variant="secondary" className="bg-secondary text-primary">
            {(data?.env_emails?.length || 0) + (data?.dynamic_emails?.length || 0)} emails
          </Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Added by</TableHead>
              <TableHead>Added at</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.env_emails || []).map((e) => (
              <TableRow key={`env-${e}`} data-testid={`allowlist-env-${e}`}>
                <TableCell className="font-mono text-sm">{e}</TableCell>
                <TableCell>
                  <Badge className="bg-accent/15 text-accent border-0 gap-1">
                    <ShieldCheck size={12}/> Env (protected)
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">System</TableCell>
                <TableCell className="text-xs text-muted-foreground">—</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {e === data?.owner ? "Owner" : "Fixed via .env"}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" disabled className="opacity-40">
                    <Lock size={14}/>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(data?.dynamic_emails || []).map((row) => (
              <TableRow key={`dyn-${row.email}`} data-testid={`allowlist-dyn-${row.email}`}>
                <TableCell className="font-mono text-sm">{row.email}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="bg-secondary text-primary">Managed</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{row.added_by || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {row.added_at ? new Date(row.added_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{row.note || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    data-testid={`allowlist-remove-${row.email}`}
                    onClick={() => setToRemove(row.email)}
                    className="text-destructive hover:bg-red-50"
                  >
                    <Trash2 size={14}/>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(!data?.env_emails?.length && !data?.dynamic_emails?.length) && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Allowlist is empty — every Google account will be able to log in.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!toRemove} onOpenChange={(o) => !o && setToRemove(null)}>
        <AlertDialogContent data-testid="allowlist-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke admin access?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{toRemove}</span> will lose access immediately. Their active
              sessions are also revoked, so they'll be logged out on their next request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="allowlist-remove-confirm-btn"
              onClick={removeEmail}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
